import { omit } from 'lodash';
import { buildMultiValueKeyFilter } from 'app/forms/config/suppressions/suppression';
import { getSuppressionReferenceIds } from '@kentik/ui-shared/alerting/suppressionReferenceIds';
import type { SuppressionKeyFilter } from './SuppressionModel';
import { unknownDimensionLabel } from './suppressionDimensions';

export type SuppressionReferenceType = 'test' | 'agent' | 'policy' | 'dimension';

export interface SuppressionReference {
  type: SuppressionReferenceType;
  id: string;
  label: string;
}

/**
 * $syn and $alerting are untyped JavaScript stores with far broader surfaces than suppressions
 * use, so these describe only the slice consumed here rather than the stores as a whole.
 * LoadState distinguishes "still loading" from "genuinely missing".
 */
export interface LoadState {
  hasFetched: boolean;
  requestStatus: string | null;
  error?: unknown;
}

export interface SuppressionPolicy {
  name?: string;
  application?: string;
  get?(field: string): string | undefined;
}

/** The part of the alerting store suppressions read: policy lookup by rule ID. */
export interface AlertingStoreSlice {
  policyCollection: LoadState & {
    getPolicyByRuleId(ruleId?: string): SuppressionPolicy | null | undefined;
  };
}

/** The part of the synthetics store suppressions read: test and agent lookup by ID. */
export interface SyntheticsStoreSlice {
  tests: (LoadState & { get(id: string): unknown }) | null;
  agents: (LoadState & { get(id: string): unknown }) | null;
}

export interface SuppressionReferenceStores {
  $syn: SyntheticsStoreSlice;
  $alerting: AlertingStoreSlice;
}

const REFERENCE_LABELS: Record<SuppressionReferenceType, { one: string; many: string }> = {
  test: { one: 'test', many: 'tests' },
  agent: { one: 'agent', many: 'agents' },
  policy: { one: 'policy', many: 'policies' },
  dimension: { one: 'dimension', many: 'dimensions' }
};

/** Shown in place of a resource the user can no longer see. Never includes the raw ID. */
export function unavailableReferenceLabel(type: SuppressionReferenceType): string {
  return `Unavailable ${REFERENCE_LABELS[type].one}`;
}

/** Collapses references into one line per type, so several unavailable resources read sensibly. */
export function unavailableReferenceSummary(references: SuppressionReference[]): string[] {
  const types = Object.keys(REFERENCE_LABELS) as SuppressionReferenceType[];

  return types.flatMap((type) => {
    const ofType = references.filter((reference) => reference.type === type);
    const count = ofType.length;

    if (!count) {
      return [];
    }

    if (type === 'dimension') {
      return [
        `Unknown ${REFERENCE_LABELS[type][count === 1 ? 'one' : 'many']}: ${ofType.map(({ id }) => id).join(', ')}`
      ];
    }

    return [count === 1 ? unavailableReferenceLabel(type) : `${count} unavailable ${REFERENCE_LABELS[type].many}`];
  });
}

export function unknownDimensionReferences(keys: string[]): SuppressionReference[] {
  return keys.map((key) => ({ type: 'dimension', id: key, label: unknownDimensionLabel(key) }));
}

type SuppressionKeyFilterItem = SuppressionKeyFilter['filters'][number];

/**
 * The IDs a single clause contributes to the selector. Empty means the clause is not owned by
 * the editor -- it is keyed on something else, or it is a negated predicate the UI cannot express.
 */
function referenceIds(filter: SuppressionKeyFilterItem, keys: string[]): string[] {
  return getSuppressionReferenceIds({ filters: [filter] }, keys);
}

export function stripOldKeyFilter(keyFilter: SuppressionKeyFilter | undefined): SuppressionKeyFilter {
  return { ...omit(keyFilter, 'filter'), filters: keyFilter?.filters ? [...keyFilter.filters] : [] };
}

export function updateReferenceKeyFilter(
  existing: SuppressionKeyFilter | undefined,
  field: string | string[],
  selectedIds: string[]
): SuppressionKeyFilter {
  const keys = Array.isArray(field) ? field : [field];
  const previous = getSuppressionReferenceIds(existing, keys);

  if (existing && previous.length === selectedIds.length && previous.every((id) => selectedIds.includes(id))) {
    return stripOldKeyFilter(existing);
  }

  const filters = existing?.filters || [];
  const [ownedClause] = filters.filter((filter) => referenceIds(filter, keys).length > 0);
  const selected: SuppressionKeyFilterItem[] = selectedIds.length
    ? buildMultiValueKeyFilter(field, selectedIds).filters
    : [];

  if (!ownedClause) {
    return { ...omit(existing, 'filter'), filters: [...filters, ...selected] };
  }

  return {
    ...omit(existing, 'filter'),
    filters: filters.flatMap((filter) => (filter === ownedClause ? selected : [filter]))
  };
}

export function getUnavailableReferences(
  { ruleIds = [], testIds = [], agentIds = [] }: { ruleIds?: string[]; testIds?: string[]; agentIds?: string[] },
  { $syn, $alerting }: SuppressionReferenceStores
): SuppressionReference[] {
  const references: SuppressionReference[] = [];

  const append = (
    type: SuppressionReferenceType,
    ids: string[],
    collection: LoadState | null,
    resolve: (id: string) => unknown
  ) => {
    if (!collection?.hasFetched || collection.requestStatus || collection.error) {
      return;
    }

    ids.forEach((id) => {
      if (!resolve(id)) {
        references.push({ type, id, label: unavailableReferenceLabel(type) });
      }
    });
  };

  if (ruleIds.length) {
    append('policy', ruleIds, $alerting.policyCollection, (id) => $alerting.policyCollection.getPolicyByRuleId(id));
  }

  if (testIds.length) {
    append('test', testIds, $syn.tests, (id) => $syn.tests?.get(id));
  }

  if (agentIds.length) {
    append('agent', agentIds, $syn.agents, (id) => $syn.agents?.get(id));
  }

  return references;
}
